/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    DeviceDetailsActionType,
    DeviceDetailsAction,
    fetchDeviceDetailsErrorAction,
    fetchDeviceDetailsSuccessAction,
    fetchFirmwareHistorySuccessAction,
    fetchFirmwareHistoryErrorAction,
    downloadFirmwareHistorySuccessAction,
    downloadFirmwareHistoryErrorAction,
    fetchStatusHistorySuccessAction,
    fetchStatusHistoryErrorAction,
    downloadStatusHistorySuccessAction,
    downloadStatusHistoryErrorAction,
    fetchOperatorHistorySuccessAction,
    fetchOperatorHistoryErrorAction,
    downloadOperatorHistorySuccessAction,
    downloadOperatorHistoryErrorAction,
    fetchRegistrationHistorySuccessAction,
    fetchRegistrationHistoryErrorAction,
    downloadRegistrationHistorySuccessAction,
    downloadRegistrationHistoryErrorAction,
} from '../actions/deviceDetailsActions';
import { catchError, exhaustMap, map, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { devicesClient } from '../api/devices/devicesClient';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { RootState } from '../store.typings';

export const getDeviceDetailsEpic: Epic<DeviceDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.OPEN_REGISTRY_DEVICE_CARD_PAGE),
        switchMap(() => {
            const deviceFactoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;

            return from(devicesClient.getDevice(deviceFactoryNumber)).pipe(
                map((resp) => fetchDeviceDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchDeviceDetailsErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.deviceNotFound', { searchText: deviceFactoryNumber }),
                            ),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(DeviceDetailsActionType.EXIT_REGISTRY_DEVICE_CARD_PAGE)),
            );
        }),
    );
};

export const fetchFirmwareHistoryEpic: Epic<DeviceDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.FETCH_FIRMWARE_HISTORY),
        switchMap((action) => {
            const params = action.payload;
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const listName = i18n.t('RegistryScreens.deviceFirmwareHistory');

            return from(devicesClient.getFirmwareHistory(factoryNumber, params)).pipe(
                map((resp) => {
                    return fetchFirmwareHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(fetchFirmwareHistoryErrorAction(parseError(err, i18n.t('errors.failedLoadList', { listName })))),
                ),

                takeUntil(action$.ofType(DeviceDetailsActionType.EXIT_REGISTRY_DEVICE_CARD_PAGE)),
            );
        }),
    );
};

export const firmwareHistoryDownloadEpic: Epic<DeviceDetailsAction, DeviceDetailsAction, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.DOWNLOAD_FIRMWARE_HISTORY),
        exhaustMap(() => {
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const fileName = `token_${factoryNumber}_FirmwareVersionsHistory.xls`;

            return from(devicesClient.downloadFirmwareHistoryAsExcel(factoryNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadFirmwareHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadFirmwareHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchStatusHistoryEpic: Epic<DeviceDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.FETCH_STATUS_HISTORY),
        switchMap((action) => {
            const params = action.payload;
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const listName = i18n.t('RegistryScreens.deviceStatusHistory');

            return from(devicesClient.getStatusHistory(factoryNumber, params)).pipe(
                map((resp) => {
                    return fetchStatusHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(fetchStatusHistoryErrorAction(parseError(err, i18n.t('errors.failedLoadList', { listName })))),
                ),

                takeUntil(action$.ofType(DeviceDetailsActionType.EXIT_REGISTRY_DEVICE_CARD_PAGE)),
            );
        }),
    );
};

export const statusHistoryDownloadEpic: Epic<DeviceDetailsAction, DeviceDetailsAction, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.DOWNLOAD_STATUS_HISTORY),
        exhaustMap(() => {
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const fileName = `token_${factoryNumber}_StatusHistory.xls`;

            return from(devicesClient.downloadStatusHistoryAsExcel(factoryNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadStatusHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadStatusHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchOperatorHistoryEpic: Epic<DeviceDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.FETCH_OPERATOR_HISTORY),
        switchMap((action) => {
            const params = action.payload;
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const listName = i18n.t('RegistryScreens.deviceOperatorHistory');

            return from(devicesClient.getOperatorHistory(factoryNumber, params)).pipe(
                map((resp) => {
                    return fetchOperatorHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(fetchOperatorHistoryErrorAction(parseError(err, i18n.t('errors.failedLoadList', { listName })))),
                ),

                takeUntil(action$.ofType(DeviceDetailsActionType.EXIT_REGISTRY_DEVICE_CARD_PAGE)),
            );
        }),
    );
};

export const operatorHistoryDownloadEpic: Epic<DeviceDetailsAction, DeviceDetailsAction, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.DOWNLOAD_OPERATOR_HISTORY),
        exhaustMap(() => {
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const fileName = `token_${factoryNumber}_OperatorsHistory.xls`;

            return from(devicesClient.downloadOperatorHistoryAsExcel(factoryNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadOperatorHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadOperatorHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchRegistrationHistoryEpic: Epic<DeviceDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.FETCH_REGISTRATION_HISTORY),
        switchMap(() => {
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const listName = i18n.t('RegistryScreens.deviceRegistrationHistory');

            return from(devicesClient.getRegistrationHistory(factoryNumber)).pipe(
                map((resp) => {
                    return fetchRegistrationHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchRegistrationHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(DeviceDetailsActionType.EXIT_REGISTRY_DEVICE_CARD_PAGE)),
            );
        }),
    );
};

export const registrationHistoryDownloadEpic: Epic<DeviceDetailsAction, DeviceDetailsAction, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(DeviceDetailsActionType.DOWNLOAD_REGISTRATION_HISTORY),
        exhaustMap(() => {
            const factoryNumber = state$.value.routerState.route.params.DeviceFactoryNumber;
            const fileName = `token_${factoryNumber}_RegistrationsHistory.xls`;

            return from(devicesClient.downloadRegistrationHistoryAsExcel(factoryNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadRegistrationHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadRegistrationHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};
